<template>
    <div :class="`single-choice single-choice-align-${align}`">
        <label :for="componentId" class="field-label">
            {{ fieldLabel }}
            <span v-if="isRequired" class="required-indicator">*</span>
        </label>
        <div class="input-container">
            <div class="radio-group">
                <div v-for="(item, index) in items" :key="itemIds[index]" class="radio-wrapper">
                    <input
                        type="radio"
                        :id="itemIds[index]"
                        :name="componentId"
                        :value="item"
                        v-model="selectedValue"
                        @change="handleChange"
                        @focus="onFocus"
                        @blur="onBlur"
                        :required="isRequired"
                    />
                    <label :for="itemIds[index]" class="radio-label" v-html="processItem(item)"></label>
                </div>
                <div v-if="otherOption" class="radio-wrapper">
                    <input
                        type="radio"
                        :id="`other-${componentId}`"
                        :name="componentId"
                        value="other"
                        v-model="selectedValue"
                        @change="handleChange"
                        @focus="onFocus"
                        @blur="onBlur"
                        :required="isRequired"
                    />
                    <label :for="`other-${componentId}`" class="radio-label">{{ otherOption }}</label>
                    <input
                        v-if="selectedValue === 'other'"
                        type="text"
                        v-model="otherText"
                        @input="handleOtherTextChange"
                        @focus="onFocus"
                        @blur="onBlur"
                        class="other-input"
                        placeholder="Diğer seçeneği girin"
                    />
                </div>
            </div>
            <div v-if="showError && isRequired && !selectedValue" class="error-message">
                {{ fieldLabel }} alanı zorunludur
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "SingleChoice",
    props: {
        fieldLabel: {
            type: String,
            required: true,
        },
        align: {
            type: String,
            default: "top",
        },
        items: {
            type: Array,
            required: true,
            default: () => [],
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        value: {
            type: String,
            default: null,
        },
        showError: {
            type: Boolean,
            default: false,
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        },
        otherOption: {
            type: String,
            default: null,
        },
        defaultValues: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            selectedValue: this.value,
            otherText: "",
            componentId: `single-choice-${Math.random().toString(36).substring(2, 15)}`,
            itemIds: [],
        };
    },
    methods: {
        generateItemIds() {
            this.itemIds = this.items.map((_, index) => `radio-${this.componentId}-${index}`);
        },
        handleChange() {
            if (this.selectedValue === "other") {
                this.$emit("input", this.otherText);
            } else {
                this.otherText = "";
                this.$emit("input", this.selectedValue);
            }
            this.validateField();
        },
        handleOtherTextChange() {
            if (this.selectedValue === "other") {
                this.$emit("input", this.otherText);
                this.validateField();
            }
        },
        validateField() {
            const hasError = this.isRequired && !this.selectedValue;
            this.$emit("validate", {
                fieldId: this._uid,
                hasError: hasError,
            });
            this.saveToLocalStorage();
        },
        saveToLocalStorage() {
            const singleChoiceData = {
                selectedValue: this.selectedValue,
                otherText: this.otherText,
                fieldLabel: this.fieldLabel,
            };
            localStorage.setItem(`${this.formId}-${this.order}`, JSON.stringify(singleChoiceData));
        },
        loadValues() {
            const savedData = localStorage.getItem(`${this.formId}-${this.order}`);
            if (savedData) {
                const parsedData = JSON.parse(savedData);
                this.selectedValue = parsedData.selectedValue || null;
                this.otherText = parsedData.otherText || "";
            }
        },
        onFocus(event) {
            event.target.closest(".radio-wrapper")?.classList.add("focused");
        },
        onBlur(event) {
            event.target.closest(".radio-wrapper")?.classList.remove("focused");
        },
        processItem(item) {
            // [text](url) markdown link'lerini ve düz URL'leri tıklanabilir <a> etiketine çevirir
            const escapeHtml = (str) => str
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
            let result = escapeHtml(item);
            const placeholders = [];
            // Markdown link: [text](url)
            result = result.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (match, linkText, url) => {
                const link = `<a href="${url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
                const placeholder = `__LINK_PLACEHOLDER_${placeholders.length}__`;
                placeholders.push(link);
                return placeholder;
            });
            // Düz URL'ler
            result = result.replace(/(https?:\/\/[^\s<]+)/g, (match, url) => {
                const link = `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
                const placeholder = `__LINK_PLACEHOLDER_${placeholders.length}__`;
                placeholders.push(link);
                return placeholder;
            });
            // Placeholder'ları gerçek link'lerle değiştir
            placeholders.forEach((link, i) => {
                result = result.replace(`__LINK_PLACEHOLDER_${i}__`, link);
            });
            return result;
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                if (this.items.includes(newValue)) {
                    this.selectedValue = newValue;
                    this.otherText = "";
                } else if (newValue === "" || newValue === null || newValue === undefined) {
                    const savedData = localStorage.getItem(`${this.formId}-${this.order}`);
                    if (!savedData && this.defaultValues && Array.isArray(this.defaultValues) && this.defaultValues.length > 0) {
                        const defaultValue = this.defaultValues[0];
                        if (this.items.includes(defaultValue)) {
                            this.selectedValue = defaultValue;
                            this.$nextTick(() => {
                                this.$emit("input", defaultValue);
                            });
                        }
                    } else if (this.selectedValue === "other") {
                        this.otherText = "";
                    }
                } else if (newValue && !this.items.includes(newValue)) {
                    this.selectedValue = "other";
                    this.otherText = newValue;
                } else {
                    this.selectedValue = null;
                    this.otherText = "";
                }
                this.validateField();
            },
        },
        showError: {
            handler(newValue) {
                if (newValue) {
                    this.validateField();
                }
            },
        },
    },
    created() {
        this.generateItemIds();
    },
    mounted() {
        const hasSlugValue = this.value && this.value !== "" && this.value !== null;

        if (!hasSlugValue) {
            this.loadValues();
        }
        this.validateField();
    },
};
</script>

<style scoped lang="scss">
/* Base SingleChoice Styles */
.single-choice {
    width: 100%;
    max-width: 700px;
    margin: clamp(0.5rem, 1.5vw, 1rem) auto;
    padding: clamp(0.625rem, 1.5vw, 0.875rem);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

    background: linear-gradient(145deg,
        rgba(255, 255, 255, 0.02) 0%,
        rgba(255, 255, 255, 0.01) 100%);
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.06);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
        background: linear-gradient(145deg,
            rgba(255, 255, 255, 0.03) 0%,
            rgba(255, 255, 255, 0.02) 100%);
    }

    .field-label {
        display: block;
        font-size: clamp(0.95rem, 2.2vw, 1.1rem);
        font-weight: 600;
        margin-bottom: clamp(0.75rem, 1.8vw, 1rem);
        color: var(--text-primary, rgba(55, 65, 81, 0.9));
        line-height: 1.4;
        letter-spacing: -0.01em;

        .required-indicator {
            color: #ef4444;
            margin-left: 0.125rem;
            font-weight: 700;
        }
    }

    .input-container {
        position: relative;
        min-width: 0; // Flex item için minimum genişlik
    }

    .radio-group {
        display: flex;
        flex-direction: column;
        gap: clamp(0.5rem, 1.2vw, 0.625rem);
    }

    .radio-wrapper {
        display: flex;
        align-items: flex-start;
        position: relative;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        padding: clamp(0.5rem, 1.2vw, 0.625rem);
        border-radius: 0.375rem;
        background: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(209, 213, 219, 0.3);

        &:hover {
            background: rgba(255, 255, 255, 0.5);
            border-color: rgba(107, 114, 128, 0.3);
        }

        &.focused {
            background: rgba(255, 255, 255, 0.7);
            border-color: var(--primary, #6366f1);
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        input[type="radio"] {
            position: relative;
            width: clamp(1rem, 2.5vw, 1.125rem);
            height: clamp(1rem, 2.5vw, 1.125rem);
            margin: 0;
            margin-right: clamp(0.5rem, 1.2vw, 0.75rem);
            margin-top: 0.125rem;
            appearance: none;
            border: 2px solid rgba(209, 213, 219, 0.8);
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.9);
            cursor: pointer;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

            &:hover {
                border-color: var(--primary, #6366f1);
                background: rgba(255, 255, 255, 1);
            }

            &:focus {
                outline: none;
                border-color: var(--primary, #6366f1);
                box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
            }

            &:checked {
                border-color: var(--primary, #6366f1);
                background: var(--primary, #6366f1);

                &::after {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 0.375rem;
                    height: 0.375rem;
                    background: white;
                    border-radius: 50%;
                }
            }
        }

        .radio-label {
            flex: 1;
            font-size: clamp(0.875rem, 1.8vw, 0.9rem);
            line-height: 1.5;
            color: var(--text-primary, rgba(55, 65, 81, 0.9));
            cursor: pointer;
            font-weight: 500;

            :deep(a) {
                color: var(--primary, #6366f1);
                text-decoration: underline;
                font-weight: 500;

                &:hover {
                    text-decoration: none;
                }
            }

            &:hover {
                color: var(--text-primary, rgba(55, 65, 81, 1));
            }
        }

        .other-input {
            margin-top: clamp(0.375rem, 1vw, 0.5rem);
            margin-left: clamp(1.5rem, 3.7vw, 1.875rem);
            width: 100%;
            padding: clamp(0.5rem, 1.2vw, 0.625rem);
            font-size: clamp(0.875rem, 1.8vw, 0.9rem);
            line-height: 1.4;
            color: var(--text-primary, rgba(55, 65, 81, 0.9));
            background: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(209, 213, 219, 0.6);
            border-radius: 0.375rem;
            box-sizing: border-box;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            font-family: inherit;
            backdrop-filter: blur(10px);

            &::placeholder {
                color: rgba(107, 114, 128, 0.6);
            }

            &:focus {
                outline: none;
                border-color: var(--primary, #6366f1);
                background: rgba(255, 255, 255, 0.95);
                box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1),
                0 2px 8px rgba(0, 0, 0, 0.05);
            }

            &:hover:not(:focus) {
                border-color: rgba(107, 114, 128, 0.4);
                background: rgba(255, 255, 255, 0.85);
            }
        }
    }

    .error-message {
        color: #ef4444;
        font-size: clamp(0.8rem, 1.8vw, 0.875rem);
        margin-top: clamp(0.5rem, 1.2vw, 0.75rem);
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 0.375rem;

        &::before {
            content: '⚠';
            font-size: 0.875em;
        }
    }
}

/* Left Alignment - Label solda, radio grup sağda */
.single-choice-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);
    text-align: left;

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.5rem, 1.2vw, 0.625rem);
        text-align: left;
    }

    .input-container {
        flex: 1;
    }

    .radio-group {
        gap: clamp(0.375rem, 1vw, 0.5rem);
    }

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;

        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.75rem, 1.8vw, 1rem);
        }

        .input-container {
            width: 100%;
        }

        .radio-group {
            gap: clamp(0.5rem, 1.2vw, 0.625rem);
        }
    }
}

/* Right Alignment - Label solda sağa yaslanmış, radio grup sağda */
.single-choice-align-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.5rem, 1.2vw, 0.625rem);
        text-align: right;
    }

    .input-container {
        flex: 1;
    }

    .radio-group {
        gap: clamp(0.375rem, 1vw, 0.5rem);
    }

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;

        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.75rem, 1.8vw, 1rem);
            text-align: left;
        }

        .input-container {
            width: 100%;
        }

        .radio-group {
            gap: clamp(0.5rem, 1.2vw, 0.625rem);
        }
    }
}

/* Center/Top Alignment (Default) */
.single-choice-align-top {
    text-align: left;
    flex-direction: column;
}

// Mobil optimizasyonları
@media (max-width: 768px) {
    .single-choice {
        margin: clamp(0.25rem, 1vw, 0.5rem) auto;
        padding: clamp(0.5rem, 1.2vw, 0.75rem);

        .field-label {
            font-size: clamp(0.9rem, 2vw, 1rem);
            margin-bottom: clamp(0.625rem, 1.5vw, 0.875rem);
        }

        .radio-group {
            gap: clamp(0.375rem, 1vw, 0.5rem);
        }

        .radio-wrapper {
            padding: clamp(0.5rem, 1.2vw, 0.625rem);

            input[type="radio"] {
                width: clamp(1rem, 2.5vw, 1.125rem);
                height: clamp(1rem, 2.5vw, 1.125rem);
                margin-right: clamp(0.5rem, 1.2vw, 0.75rem);
            }

            .radio-label {
                font-size: clamp(0.875rem, 2vw, 0.9rem);
            }

            .other-input {
                margin-top: clamp(0.375rem, 1vw, 0.5rem);
                margin-left: clamp(1.5rem, 3.7vw, 1.875rem);
                padding: clamp(0.5rem, 1.2vw, 0.625rem);
                font-size: clamp(0.875rem, 2vw, 0.9rem);
                border-radius: 0.5rem;
            }
        }

        .error-message {
            font-size: clamp(0.8rem, 1.8vw, 0.85rem);
            margin-top: clamp(0.375rem, 1vw, 0.5rem);
        }
    }
}

@media (max-width: 480px) {
    .single-choice {
        margin: 0.25rem auto;
        padding: 0.5rem;

        .field-label {
            font-size: 0.9rem;
            margin-bottom: 0.625rem;
        }

        .radio-group {
            gap: 0.375rem;
        }

        .radio-wrapper {
            padding: 0.5rem;

            input[type="radio"] {
                width: 1rem;
                height: 1rem;
                margin-right: 0.5rem;
            }

            .radio-label {
                font-size: 0.875rem;
            }

            .other-input {
                margin-top: 0.375rem;
                margin-left: 1.5rem;
                padding: 0.5rem;
                font-size: 0.875rem;
            }
        }

        .error-message {
            font-size: 0.8rem;
            margin-top: 0.375rem;
        }
    }
}

/* Dark mode devre dışı - Her zaman açık tema kullan */

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
    .single-choice,
    .radio-wrapper,
    .radio-wrapper input,
    .radio-label,
    .other-input {
        transition: none;
    }
}

/* High Performance Mode */
.radio-wrapper {
    will-change: transform, background-color, border-color;

    input[type="radio"] {
        will-change: border-color, background-color, box-shadow;
    }

    .other-input {
        will-change: border-color, background-color, box-shadow;
    }
}
</style>